
import React, { useState, useEffect } from 'react';
import {
    updateStudent,
    fetchStudentTypes,
    fetchSexes,
    fetchCountries,
    fetchStates,
    deleteStudentAddressUS,
    deleteStudentAddressHC,
} from '../utils/api';
import { mapErrorMessage } from '../utils/errorMapping';
import './_Modal.css';

const EditStudentModal = ({ student, onClose, onStudentUpdated }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [sex, setSex] = useState('');
    const [studentType, setStudentType] = useState('');
    const [studentTypes, setStudentTypes] = useState({});
    const [sexes, setSexes] = useState({});
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [showAddressUS, setShowAddressUS] = useState(false);
    const [addressUS, setAddressUS] = useState({ line: '', city: '', state: '', zip_code: '' });
    const [showAddressHC, setShowAddressHC] = useState(false);
    const [addressHC, setAddressHC] = useState({ line: '', city: '', province: '', country: '', zip_code: '' });
    const [birthdate, setBirthdate] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [typesData, sexesData, countriesData, statesData] = await Promise.all([
                    fetchStudentTypes(),
                    fetchSexes(),
                    fetchCountries(),
                    fetchStates(),
                ]);
                setStudentTypes(typesData.data);
                setSexes(sexesData.data);
                setCountries(countriesData.data);
                setStates(statesData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        if (student) {
            setFirstName(student.first_name || '');
            setLastName(student.last_name || '');
            setMiddleName(student.middle_name || '');
            setEmail(student.email || '');
            setPhone(student.phone || '');
            setSex(student.sex?.id || '');
            setStudentType(student.type?.id || '');
            if (student.address_us) {
                setAddressUS({
                    line: student.address_us.line || '',
                    city: student.address_us.city || '',
                    state: student.address_us.state?.id || '',
                    zip_code: student.address_us.zip_code || '',
                });
                setShowAddressUS(true);
            }
            if (student.address_hc) {
                setAddressHC({
                    line: student.address_hc.line || '',
                    city: student.address_hc.city || '',
                    province: student.address_hc.province || '',
                    country: student.address_hc.country?.id || '',
                    zip_code: student.address_hc.zip_code || '',
                });
                setShowAddressHC(true);
            }
            setBirthdate(student.birthdate ? student.birthdate.split('T')[0] : '');
        }
    }, [student]);

    const handleAddressUSChange = (e) => {
        setAddressUS({ ...addressUS, [e.target.name]: e.target.value });
    };

    const handleAddressHCChange = (e) => {
        setAddressHC({ ...addressHC, [e.target.name]: e.target.value });
    };

    const handleDeleteAddressUS = async () => {
        if (!window.confirm('Are you sure you want to delete the US address?')) return;
        try {
            await deleteStudentAddressUS(student.id);
            onStudentUpdated();
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleDeleteAddressHC = async () => {
        if (!window.confirm('Are you sure you want to delete the Home Country address?')) return;
        try {
            await deleteStudentAddressHC(student.id);
            onStudentUpdated();
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!firstName || !lastName || !email || !studentType) {
            setError('Please fill in all required fields.');
            return;
        }

        const body = {
            first_name: firstName,
            last_name: lastName,
            email: email,
            type: parseInt(studentType, 10),
        };

        if (middleName) body.middle_name = middleName;
        if (phone) body.phone = phone;
        if (sex) body.sex = parseInt(sex, 10);
        if (birthdate) body.birthdate = birthdate;

        if (showAddressUS) {
            body.address_us = {
                line: addressUS.line,
                city: addressUS.city,
                state: parseInt(addressUS.state, 10),
                zip_code: addressUS.zip_code,
            };
        }

        if (showAddressHC) {
            body.address_hc = {
                line: addressHC.line,
                city: addressHC.city,
                province: addressHC.province,
                country: parseInt(addressHC.country, 10),
                zip_code: addressHC.zip_code,
            };
        }

        try {
            await updateStudent(student.id, body);
            onStudentUpdated();
            onClose();
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>X</button>
                <h2>Edit Student</h2>
                {error && <p className="error">{error}</p>}
                <form onSubmit={handleUpdateSubmit}>
                    <div className="form-group">
                        <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First Name" />
                    </div>
                    <div className="form-group">
                        <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last Name" />
                    </div>
                    <div className="form-group">
                        <input type="text" value={middleName} onChange={(e) => setMiddleName(e.target.value)} placeholder="Middle Name" />
                    </div>
                    <div className="form-group">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
                    </div>
                    <div className="form-group">
                        <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
                    </div>
                    <div className="form-group">
                        <label for="birthdate">Birthdate</label>
                        <input id="birthdate" type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <select value={sex} onChange={(e) => setSex(e.target.value)}>
                            <option value="">Select Sex</option>
                            {Object.entries(sexes).map(([id, name]) => (
                                <option key={id} value={id}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <select value={studentType} onChange={(e) => setStudentType(e.target.value)}>
                            <option value="">Select Type</option>
                            {Object.entries(studentTypes).map(([id, name]) => (
                                <option key={id} value={id}>{name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="collapsible-header">
                        <button type="button" className="collapsible-button" onClick={() => setShowAddressUS(!showAddressUS)}>
                            {showAddressUS ? 'Hide' : 'Show'} US Address
                        </button>
                        {student.address_us && (
                            <button type="button" className="delete-address-button" onClick={handleDeleteAddressUS}>
                                Delete
                            </button>
                        )}
                    </div>
                    {showAddressUS && (
                        <div className="collapsible-content">
                            <div className="form-group">
                               <input type="text" name="line" value={addressUS.line} onChange={handleAddressUSChange} placeholder="Line" />
                           </div>
                           <div className="form-group">
                               <input type="text" name="city" value={addressUS.city} onChange={handleAddressUSChange} placeholder="City" />
                           </div>
                           <div className="form-group">
                               <select name="state" value={addressUS.state} onChange={handleAddressUSChange}>
                                   <option value="">Select State</option>
                                   {states.map((s) => (
                                       <option key={s.id} value={s.id}>{s.full_name}</option>
                                   ))}
                               </select>
                           </div>
                           <div className="form-group">
                               <input type="text" name="zip_code" value={addressUS.zip_code} onChange={handleAddressUSChange} placeholder="Zip Code" />
                            </div>
                        </div>
                    )}

                    <div className="collapsible-header">
                        <button type="button" className="collapsible-button" onClick={() => setShowAddressHC(!showAddressHC)}>
                            {showAddressHC ? 'Hide' : 'Show'} Home Country Address
                        </button>
                        {student.address_hc && (
                            <button type="button" className="delete-address-button" onClick={handleDeleteAddressHC}>
                                Delete
                            </button>
                        )}
                    </div>
                    {showAddressHC && (
                        <div className="collapsible-content">
                            <div className="form-group">
                               <input type="text" name="line" value={addressHC.line} onChange={handleAddressHCChange} placeholder="Line" />
                           </div>
                           <div className="form-group">
                               <input type="text" name="city" value={addressHC.city} onChange={handleAddressHCChange} placeholder="City" />
                           </div>
                           <div className="form-group">
                               <input type="text" name="province" value={addressHC.province} onChange={handleAddressHCChange} placeholder="Province" />
                           </div>
                           <div className="form-group">
                               <select name="country" value={addressHC.country} onChange={handleAddressHCChange}>
                                   <option value="">Select Country</option>
                                   {countries.map((c) => (
                                       <option key={c.id} value={c.id}>{c.full_name}</option>
                                   ))}
                               </select>
                           </div>
                           <div className="form-group">
                               <input type="text" name="zip_code" value={addressHC.zip_code} onChange={handleAddressHCChange} placeholder="Zip Code" />
                            </div>
                        </div>
                    )}
                    <div className="actions-centered">
                        <button type="button" onClick={onClose} className="cancel-button text-button">Cancel</button>
                        <button type="submit" className="submit-button text-button">Update</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditStudentModal;
